import { useEffect, useMemo } from 'react'
import * as React from 'react'
import type { OfferPageMultiRoomAutoselectionReason, OfferPageMultiRoomMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useQueryParams from 'hooks/useQueryParams'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import uuidV4 from 'lib/string/uuidV4Utils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import { GOOGLE_ADS_UTM_CONTENT } from 'constants/bedbank'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import BedbankMultiIndividualRoomRoom from './MultiRoom/BedbankMultiIndividualRoomRoom'
import BedbankMultiIndividualRoomsFooter from './MultiRoom/BedbankMultiIndividualRoomsFooter'
import PreSelectionBanner from '../MultipleRoomsBooking/PreSelectionBanner'
import InvalidSelectionBanner from '../MultipleRoomsBooking/InvalidSelectionBanner'
import { useRoomTypeAvailabilityStatuses } from 'hooks/useBedbankPackageViews'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { scrollToId } from 'lib/window/scrollUtils'
import { useIsMobileDevice } from 'hooks/useIsMobileDevice'
import { RateImpressionProvider } from 'contexts/RateImpressionContext'
import getObjectKey from 'lib/object/getObjectKey'
import BedbankMultiSameRoomRoom from './MultiRoom/BedbankMultiSameRoomRoom'
import useBedbankMetasearchPriceAccuracyTracking from 'hooks/OfferPage/useBedbankMetasearchPriceAccuracyTracking'
import { sortBedbankPackageViews } from 'lib/hotels/bedbankUtils'

interface Props {
  rooms: Array<OfferPageRoom>
  checkInDate?: string
  checkOutDate?: string
  offer: App.BedbankOffer
  cheapestFlightPrice?: number
  onUserAnalyticsAction?: (action: string, label?: string) => void
  onViewPackageOptions: () => void
  packageViews: Array<App.BedbankOfferPackageView>
  fetchingRates?: boolean
  roomMode: OfferPageMultiRoomMode
  autoselectionReason?: OfferPageMultiRoomAutoselectionReason
  multipleRoomBookingActive: boolean
}

function BedbankRoomOptionsDesktop(props: Props) {
  const {
    rooms,
    checkInDate,
    checkOutDate,
    offer,
    onViewPackageOptions,
    packageViews,
    fetchingRates,
    multipleRoomBookingActive,
    roomMode,
    autoselectionReason,
  } = props

  const query = useQueryParams()
  const validCheckinCheckoutDates = !!(checkInDate && checkOutDate)
  const highlightedRoomTypeId = query.get('roomTypeId')
  const utmContent = query.get('utm_content')
  const ghaPrice = query.get('gha_price')
  const metasearchRoomRateId = query.get('roomRateId') || query.get('roomOptionRateId') || undefined
  const isMobileDevice = useIsMobileDevice()
  const roomOptionRateId = (() => {
    const value = query.get('roomOptionRateId')
    if (isMobileDevice) {
      return `mobile-${value}`
    }
    return value === '' ? undefined : value
  })()
  useEffect(() => {
    if (!roomOptionRateId) {
      return
    }

    let mutationObserver: MutationObserver | undefined
    let cleanupTimeoutId: ReturnType<typeof setTimeout> | undefined
    const observerCleanupDelayMs = 10000
    const cleanup = () => {
      if (cleanupTimeoutId) {
        clearTimeout(cleanupTimeoutId)
        cleanupTimeoutId = undefined
      }
      if (mutationObserver) {
        mutationObserver.disconnect()
        mutationObserver = undefined
      }
    }

    const attemptScroll = () => {
      const anchorElement = document.getElementById(roomOptionRateId)
      if (!anchorElement) {
        return false
      }

      scrollToId(roomOptionRateId, 0)
      cleanup()
      return true
    }

    if (!attemptScroll()) {
      if (typeof MutationObserver !== 'undefined') {
        mutationObserver = new MutationObserver(() => {
          attemptScroll()
        })
        mutationObserver.observe(document.body, { childList: true, subtree: true })
        cleanupTimeoutId = setTimeout(() => {
          cleanup()
        }, observerCleanupDelayMs)
      }
    }

    return cleanup
  }, [roomOptionRateId])

  const hasPromotions = offer.promotions.length > 0

  const roomTypeAvailabilityStatus = useRoomTypeAvailabilityStatuses(offer, rooms, packageViews)

  const sortedPackages = useMemo(() => {
    const sortedPackageViews = sortBedbankPackageViews(packageViews, {
      hasDates: validCheckinCheckoutDates,
      hasPromotions,
    })

    const highlightedPackage = sortedPackageViews.find(p => p.package.roomId === highlightedRoomTypeId)
    if (utmContent === GOOGLE_ADS_UTM_CONTENT && highlightedPackage) {
      return [
        highlightedPackage,
        ...sortedPackageViews.filter(p => p.package.roomId !== highlightedRoomTypeId),
      ]
    }
    else {
      return sortedPackageViews
    }
  }, [packageViews, utmContent, hasPromotions, validCheckinCheckoutDates, highlightedRoomTypeId])

  const displayablePackages = useMemo(() => {
    const showAll = sortedPackages.filter(p => p.available).length === 0
    return sortedPackages.filter(packageView => packageView.show && (roomTypeAvailabilityStatus[packageView.package.roomId] !== 'unavailable' || showAll))
  }, [sortedPackages, roomTypeAvailabilityStatus])

  useBedbankMetasearchPriceAccuracyTracking({
    checkInDate,
    checkOutDate,
    displayablePackages,
    ghaPrice,
    metasearchRoomRateId,
    offer,
    rooms,
  })

  const roomListId = useMemo(() => uuidV4(),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [displayablePackages]) // regenerate id if packages change

  const roomsAreSelected = rooms.some(room => room.roomId)
  let selectionBanner: React.ReactNode = null
  if (roomsAreSelected && roomMode === 'individual') {
    if (autoselectionReason === 'initial-selection') {
      selectionBanner = <PreSelectionBanner saleUnit={offer.saleUnit} />
    } else if (autoselectionReason === 'invalid-selections') {
      selectionBanner = <InvalidSelectionBanner saleUnit={offer.saleUnit} />
    }
  }

  const availabilityKey = getObjectKey({
    checkIn: checkInDate,
    checkOut: checkOutDate,
    rooms,
  })

  const hasHighlightedPackage = utmContent === GOOGLE_ADS_UTM_CONTENT && displayablePackages[0]?.package.roomId === highlightedRoomTypeId

  useEffect(() => {
    if (hasHighlightedPackage) {
      onViewPackageOptions()
    }
  }, [hasHighlightedPackage]) // oxlint-disable-line react-hooks/exhaustive-deps

  if (multipleRoomBookingActive) {
    return <RateImpressionProvider availabilityKey={availabilityKey}>
      <VerticalSpacer gap={20}>
        {rooms.map((room, i) => {
          const roomTile = <BedbankMultiIndividualRoomRoom
            key={room.id}
            room={room}
            packageNumber={i + 1}
          />
          if (i === 0 && selectionBanner) {
            return <div key={room.id}>
              {selectionBanner}
              {roomTile}
            </div>
          } else {
            return roomTile
          }
        })}
        <BedbankMultiIndividualRoomsFooter />
      </VerticalSpacer>
    </RateImpressionProvider>
  } else {
    return <RateImpressionProvider availabilityKey={availabilityKey}>
      <VerticalSpacer gap={32}>
        {displayablePackages.map((packageView, index) => (
          <BedbankMultiSameRoomRoom
            key={packageView.package.roomId}
            propertyId={offer.id}
            packageView={packageView}
            fetchingRates={fetchingRates}
            roomListId={roomListId}
            roomPosition={index + 1}
            youWereInterested={utmContent === GOOGLE_ADS_UTM_CONTENT && highlightedRoomTypeId === packageView.package.roomId}
            availabilityStatus={roomTypeAvailabilityStatus[packageView.package.roomId]}
            ratesLayout="stacked"
          />
        ))}
      </VerticalSpacer>
    </RateImpressionProvider>
  }
}

type MappedProps = Pick<Props,
  | 'checkInDate'
  | 'checkOutDate'
  | 'roomMode'
  | 'rooms'
  | 'autoselectionReason'
  | 'multipleRoomBookingActive'
> & { open: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => {
  return {
    checkInDate: state.checkInDate,
    checkOutDate: getCheckoutDate(state),
    open: !!state.activeFilter,
    roomMode: state.multiRoomMode,
    rooms: state.rooms,
    autoselectionReason: state.multiRoomAutoselectionReason,
    multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
  }
}, BedbankRoomOptionsDesktop, preventRenderWithOpenFilter)
